import { X } from "lucide-react";
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

const ItemsOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "orderDate",
    direction: "desc",
  });
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const ordersResponse = await fetch("https://octoberfour-60f2c682a1d1.herokuapp.com/item-orders");
        if (!ordersResponse.ok) {
          throw new Error(`HTTP error! Status: ${ordersResponse.status}`);
        }

        const ordersData = await ordersResponse.json();

        // Sort by latest createdAt first
        const sortedOrders = ordersData.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA; // Descending order (latest first)
        });

        setOrders(sortedOrders);
        setError(null);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Sorting function
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted and filtered orders
  const getSortedAndFilteredOrders = () => {
    let filteredOrders = [...orders];

    // Apply search filter
    if (searchTerm) {
      filteredOrders = filteredOrders.filter(
        (order) =>
          order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.paymentStatus
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.orderStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.packageName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredOrders.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle date sorting
        if (sortConfig.key === "orderDate") {
          aValue = new Date(a.orderDate);
          bValue = new Date(b.orderDate);
        }

        // Handle numeric sorting
        if (sortConfig.key === "totalPrice") {
          aValue = parseFloat(a.totalPrice);
          bValue = parseFloat(b.totalPrice);
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredOrders;
  };

  // Get current orders for pagination
  const sortedAndFilteredOrders = getSortedAndFilteredOrders();
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedAndFilteredOrders.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalPages = Math.ceil(sortedAndFilteredOrders.length / ordersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Function to determine badge color based on order status
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-success";
      case "processing":
        return "bg-warning text-dark";
      case "shipped":
        return "bg-info text-dark";
      case "cancelled":
        return "bg-danger";
      case "paid":
        return "bg-success";
      case "pending":
        return "bg-warning text-dark";
      default:
        return "bg-secondary";
    }
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    try {
      if (dateString.includes("T")) {
        // If it's an ISO date string
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      // If it's already in a readable format like "02/07/2025"
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? " ↑" : " ↓";
    }
    return "";
  };

  const handleDeleteOrder = async (orderId) => {
    setDeletingOrderId(orderId);
    try {
      const res = await fetch(`https://octoberfour-60f2c682a1d1.herokuapp.com/item-order/${orderId}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (data.success) {
        setOrders((prev) => prev.filter((order) => order._id !== orderId));
        toast.success("Order deleted successfully!");
      } else {
        toast.error("Failed to delete order.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Something went wrong.");
    } finally {
      setDeletingOrderId(null);
    }
  };

  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col">
          <h2 className="fw-bold" style={{ color: "#fff" }}>
            Item Orders Management
          </h2>
          <p className="text-muted">View and manage all customer item orders</p>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col">
          <div
            className="card shadow-sm border-0"
            style={{ borderRadius: "10px" }}
          >
            <div
              className="card-header bg-white d-flex justify-content-between align-items-center py-3"
              style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
            >
              <h5 className="mb-0 fw-bold" style={{ color: "#3a3a3a" }}>
                All Item Orders
              </h5>
              <div className="d-flex">
                <div className="input-group me-2" style={{ maxWidth: "250px" }}>
                  <input
                    type="text"
                    className="form-control border-end-0"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    style={{ borderRadius: "5px 0 0 5px" }}
                  />
                  <span
                    className="input-group-text bg-white border-start-0"
                    style={{ borderRadius: "0 5px 5px 0" }}
                  >
                    <span>🔍</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading orders...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-3" role="alert">
                  {error}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table
                      className="table table-hover mb-0"
                      style={{ borderCollapse: "separate", borderSpacing: "0" }}
                    >
                      <thead>
                        <tr className="bg-light">
                          <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("orderId")}
                          >
                            Order ID {getSortIndicator("orderId")}
                          </th>
                          <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("customerName")}
                          >
                            Customer {getSortIndicator("customerName")}
                          </th>
                          <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("packageName")}
                          >
                            Item {getSortIndicator("packageName")}
                          </th>
                          <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("orderDate")}
                          >
                            Date {getSortIndicator("orderDate")}
                          </th>
                          <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("totalPrice")}
                          >
                            Amount {getSortIndicator("totalPrice")}
                          </th>
                          <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("paymentStatus")}
                          >
                            Payment Status {getSortIndicator("paymentStatus")}
                          </th>
                          {/* <th
                            className="px-4 py-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => requestSort("orderStatus")}
                          >
                            Order Status {getSortIndicator("orderStatus")}
                          </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.length === 0 ? (
                          <tr>
                            <td
                              colSpan="8"
                              className="text-center py-4 text-muted"
                            >
                              No orders found
                            </td>
                          </tr>
                        ) : (
                          currentOrders.map((order) => (
                            <tr
                              key={order._id}
                              style={{ transition: "all 0.2s ease" }}
                            >
                              <td className="px-4 py-3 align-middle fw-bold">
                                #{order.orderId}
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <div className="d-flex align-items-center">
                                  <div
                                    className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                                    style={{
                                      width: "36px",
                                      height: "36px",
                                      fontSize: "16px",
                                    }}
                                  >
                                    {order.customerName.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="fw-semibold">
                                      {order.customerName}
                                    </div>
                                    <small className="text-muted">
                                      {order.customerEmail}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <div className="fw-semibold text-primary">
                                  <Link
                                    to={`/premium/${order.slug}/${order.itemId}`}
                                  >
                                    {order.packageName.length > 30
                                      ? `${order.packageName.slice(0, 30)}...`
                                      : order.packageName}
                                  </Link>
                                </div>
                                <small className="text-muted">
                                  ID: {order.itemId || "N/A"}
                                </small>
                              </td>

                              <td className="px-4 py-3 align-middle">
                                {formatDate(order.orderDate)}
                              </td>
                              <td className="px-4 py-3 align-middle fw-bold">
                                ${order.totalPrice} USD
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <span
                                  className={`badge ${getStatusBadgeClass(
                                    order.paymentStatus
                                  )} rounded-pill px-3 py-2`}
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {order.paymentStatus.toUpperCase()}
                                </span>
                                {order.paymentStatus.toLowerCase() ===
                                  "pending" &&
                                  (deletingOrderId === order._id ? (
                                    <div
                                      className="spinner-border spinner-border-sm text-danger ms-2"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Deleting...
                                      </span>
                                    </div>
                                  ) : (
                                    <X
                                      className="ms-2"
                                      color="red"
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        handleDeleteOrder(order._id)
                                      }
                                    />
                                  ))}
                              </td>
                              {/* <td className="px-4 py-3 align-middle">
                                <span
                                  className={`badge ${getStatusBadgeClass(
                                    order.orderStatus
                                  )} rounded-pill px-3 py-2`}
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {order.orderStatus.toUpperCase()}
                                </span>
                              </td> */}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="text-muted">
                      Showing {indexOfFirstOrder + 1} to{" "}
                      {Math.min(
                        indexOfLastOrder,
                        sortedAndFilteredOrders.length
                      )}{" "}
                      of {sortedAndFilteredOrders.length} entries
                    </div>
                    <nav aria-label="Page navigation">
                      <ul className="pagination mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{ border: "none" }}
                          >
                            Previous
                          </button>
                        </li>
                        {Array.from({ length: totalPages }).map((_, index) => (
                          <li
                            key={index}
                            className={`page-item ${
                              currentPage === index + 1 ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => paginate(index + 1)}
                              style={{ border: "none" }}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{ border: "none" }}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemsOrders;
